import React, { useState, useContext, useRef, useEffect } from 'react'
import { isEmpty } from 'ramda'
import styled from 'styled-components'
import {
  Box,
  Flex,
  FormField,
  Label,
  Select,
  getPaletteColor,
  Text
} from 'pcln-design-system'
import { useRouter } from 'next/router'
import { Calendar } from '@pcln/date-picker'
import { AirPricedCalendar } from '@pcln/fly-date-picker'
import { useForm } from 'react-hook-form'
import CalendarWrapperBox from '../../CalenderWrapperBox'
import DateField from '../../DateField'
import ShadowEffect from '../../ShadowEffect'
import TypeAhead from '../../TypeAhead'
import { PopoverContainer } from '../../Popover'
import { FLIGHTS_DISCLAIMER } from '../../TravelerSelection/constants'
import TravelerSelection from '../../TravelerSelection/TravelerSelection'
import BootstrapDataContext from '../../../context/BootstrapDataContext'
import useSeti from '../../CustomHooks/useSeti'
import {
  setFlights,
  getPrevDate,
  isRoundTrip,
  isOneWay,
  isMultiDestination,
  includesStay,
  isPackage,
  fields,
  initialState,
  flightsStartLocationSearchKey,
  flightsEndLocationSearchKey,
  flightsDateRangeKey
} from './utils'
import FlightTypeRadios from './FlightTypeRadios'
import searchFormSchema from './validation'
import SearchFormButton from '../../SearchFormButton'
import { getDateRange } from '../../../utils/date-helper'
import {
  getDateFieldBorderColors,
  getDateFieldErrorMessage
} from '../../../utils/error-helpers'
import {
  FlightRecordType,
  FlightFormStateType,
  CABIN_CLASS_TYPE,
  FLIGHT_TYPE_INFO as FLIGHT_TYPE_INFO_TYPE,
  FlightDateType
} from '../types'
import { LOCATION_SEARCH_TYPE } from '../../../types'
import {
  ADD_PRODUCT_OPTIONS,
  CABIN_CLASS,
  FLIGHT_TYPE_INFO,
  HOTEL,
  MULTI_DESTINATION,
  ONE_WAY,
  ROUND_TRIP
} from '../constants'
import MultiDestination from './MultiDestination'
import ProductCheckBox from '../../ProductCheckbox/ProductCheckbox'
import analytics from '../../../utils/analytics'
import {
  ANALYTICS_ACTION_FLIGHTS_SEARCH,
  ANALYTICS_CATEGORIES,
  INVALID
} from '../../../constants/analytics'
import { prepopulateFlightsValues } from '../../../utils/prepop-helper'
import { FlightsQuery } from '../../../utils/typeahead-fetch-types'

Label.nowrap = styled(Label)`
  white-space: nowrap;
`

const StyledSelect = styled<{ isMobile: boolean; [key: string]: unknown }>(
  Select
)`
  background-color: ${getPaletteColor('background.lightest')};
  cursor: pointer;
  height: ${props => (props.isMobile ? undefined : '56px')};
`

const StyledButtonContainer = styled(Box)`
  position: relative;
`

type FormType = {
  onSubmit: (data: FlightFormStateType) => void
  flightTypes?: FLIGHT_TYPE_INFO_TYPE[]
  addPackagesSupport?: boolean
  promoDeal?: boolean
}

// Needed to prevent prop type errors with a js component and typescript
const TypeAheadJS: any = TypeAhead
const DateFieldJS: any = DateField

export default function Form({
  onSubmit,
  flightTypes = [
    FLIGHT_TYPE_INFO[ROUND_TRIP],
    FLIGHT_TYPE_INFO[ONE_WAY],
    FLIGHT_TYPE_INFO[MULTI_DESTINATION]
  ],
  promoDeal
}: FormType) {
  const {
    errors,
    handleSubmit,
    register,
    setValue,
    triggerValidation,
    unregister,
    watch
  } = useForm<FlightFormStateType>({
    defaultValues: initialState,
    mode: 'onSubmit',
    validationSchema: searchFormSchema,
    reValidateMode: 'onChange'
  })
  const {
    adultCount: watchAdultCount,
    cabinClass: watchCabinClass,
    childrenAges: watchChildrenAges,
    childrenCount: watchChildrenCount,
    infantCount: watchInfantCount,
    flightType: watchFlightType,
    flights: watchFlights,
    roomCount: watchRoomCount,
    tripType: watchTripType,
    youthCount: watchYouthCount
  } = watch({ nest: true })

  const { query, pathname } = useRouter()
  const ANALYTICS_CATEGORY = ANALYTICS_CATEGORIES[pathname]

  useEffect(() => {
    fields.forEach(name => {
      register({ name })
    })
    return () => {
      fields.forEach(name => {
        unregister(name)
      })
    }
  }, [register, unregister])

  useEffect(() => {
    prepopulateFlightsValues(query as FlightsQuery, setValue)
  }, [query, setValue])

  useEffect(() => {
    triggerValidation('roomCount')
  }, [
    triggerValidation,
    watchAdultCount,
    watchChildrenCount,
    watchInfantCount,
    watchTripType,
    watchRoomCount,
    watchYouthCount
  ])
  useEffect(() => {
    if (!isEmpty(errors)) {
      triggerValidation('flights')

      let gaLabel = `${INVALID} -`
      if (errors.flights) {
        errors.flights.forEach((flight, index) => {
          const invalidFlightFields = Object.keys(flight)
          gaLabel += ` Flight ${index + 1}: ${invalidFlightFields.join('| ')}`
        })
      }
      if (errors.roomCount) gaLabel += ' | roomCount'
      if (errors.infantCount) gaLabel += ' | infantCount'

      analytics.fireEvent({
        action: ANALYTICS_ACTION_FLIGHTS_SEARCH,
        category: ANALYTICS_CATEGORY,
        label: gaLabel
      })
    }
  }, [errors, triggerValidation, watchFlights])
  useEffect(() => {
    triggerValidation('infantCount')
  }, [
    triggerValidation,
    watchFlights,
    watchInfantCount,
    watchAdultCount,
    watchChildrenAges
  ])
  const { isMobile = false } = useContext(BootstrapDataContext)
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false)
  const [
    departureAirport,
    setDepartureAirport
  ] = useState<LOCATION_SEARCH_TYPE | null>(null)
  const [
    arrivalAirport,
    setArrivalAirport
  ] = useState<LOCATION_SEARCH_TYPE | null>(null)
  const calendarInput = useRef<HTMLInputElement | null>(null)
  const typeaheadWidth = [1, null, 1 / 2, null, 1 / 3]
  const startLocationPlaceholder = 'Departing from?'
  const endLocationPlaceholder = 'Going to?'
  const isFlightTypeRoundTrip = isRoundTrip(watchFlightType)
  const isFlightTypeOneWay = isOneWay(watchFlightType)
  const isFlightTypeMultiDestination = isMultiDestination(watchFlightType)
  const isFlightTypeIncludeStay = includesStay(watchFlightType)
  const showCabinClass =
    (!isFlightTypeRoundTrip || !isPackage(watchTripType)) && !promoDeal
  const showPackages = isFlightTypeRoundTrip && !promoDeal
  const firstFlight: Partial<FlightRecordType> = watchFlights[0] || {}
  const datePickerLabel = isFlightTypeRoundTrip
    ? 'Departing - Returning'
    : 'Departing'
  const datePickerAriaLabel =
    isFlightTypeOneWay || isFlightTypeMultiDestination
      ? 'Departing Date Picker'
      : 'Departing – Returning Date Picker'

  const AIR_HOME_PRICED_CALENDAR_MOBILE = useSeti(
    'AIR_HOME_PRICED_CALENDAR_MOBILE',
    isMobile && !isFlightTypeMultiDestination
  )
  const isPricedCalendarActive =
    !isFlightTypeMultiDestination &&
    (!isMobile || AIR_HOME_PRICED_CALENDAR_MOBILE === 'VARIANT')

  const CalendarComponent = isPricedCalendarActive
    ? AirPricedCalendar
    : Calendar

  const focusCalendarInput = (): void => {
    if (calendarInput && calendarInput.current) {
      calendarInput.current.focus()
    }
  }

  const showPackageOptions = () =>
    showPackages && (
      <ProductCheckBox
        category={ANALYTICS_CATEGORY}
        productOptions={ADD_PRODUCT_OPTIONS}
        tripType={watchTripType}
        label="Need anything else?"
        setValue={setValue}
      />
    )
  return (
    <form
      onSubmit={handleSubmit(values =>
        onSubmit({ ...values, gaCategory: ANALYTICS_CATEGORY })
      )}
      data-testid={promoDeal ? 'express-deal-form' : 'form'}
    >
      <Flex mb={2}>
        <FlightTypeRadios
          flightTypesToDisplay={flightTypes}
          flightType={watchFlightType}
          setValue={setValue}
          register={register}
          flights={watchFlights}
        />
      </Flex>
      <Flex key={0} mx={-2} flexWrap="wrap">
        <Box width={typeaheadWidth} p={2}>
          <ShadowEffect enabled forProperty={flightsStartLocationSearchKey(0)}>
            {({ disableShadowState }: { disableShadowState: () => void }) => (
              <TypeAheadJS
                searchProduct="flights"
                placeholder={startLocationPlaceholder}
                label={startLocationPlaceholder}
                searchKey={flightsStartLocationSearchKey(0)}
                errors={errors}
                defaultSelectedItem={firstFlight?.startLocation ?? {}}
                disableShadowState={disableShadowState}
                onItemSelect={(item: LOCATION_SEARCH_TYPE) => {
                  setFlights(
                    watchFlights,
                    {
                      startLocation: item
                    },
                    setValue
                  )
                  setDepartureAirport(item)
                }}
              />
            )}
          </ShadowEffect>
        </Box>
        <Box width={typeaheadWidth} p={2}>
          <ShadowEffect enabled forProperty={flightsEndLocationSearchKey(0)}>
            {({ disableShadowState }: { disableShadowState: () => void }) => (
              <TypeAheadJS
                searchProduct="flights"
                placeholder={endLocationPlaceholder}
                label={endLocationPlaceholder}
                searchKey={flightsEndLocationSearchKey(0)}
                errors={errors}
                defaultSelectedItem={firstFlight?.endLocation ?? {}}
                disableShadowState={disableShadowState}
                onItemSelect={(item: LOCATION_SEARCH_TYPE) => {
                  setFlights(
                    watchFlights,
                    {
                      endLocation: item
                    },
                    setValue
                  )
                  setArrivalAirport(item)
                }}
              />
            )}
          </ShadowEffect>
        </Box>
        <Flex
          alignItems="center"
          justifyContent="stretch"
          width={[1, null, null, null, 1 / 3]}
          px={2}
          py={2}
        >
          <ShadowEffect forProperty={flightsDateRangeKey(0)}>
            {({ disableShadowState }: { disableShadowState: () => void }) => (
              <PopoverContainer
                key="dates"
                data-calendar
                open={isCalendarOpen}
                position={promoDeal ? 'left' : 'right'}
                onDismiss={() => {
                  setIsCalendarOpen(false)
                  disableShadowState()
                }}
              >
                <Box>
                  <DateFieldJS
                    m={0}
                    width={1 / 2}
                    id={flightsDateRangeKey(0)}
                    name={flightsDateRangeKey(0)}
                    ref={(elem: HTMLInputElement) => {
                      calendarInput.current = elem
                    }}
                    label={datePickerLabel}
                    placeholder={datePickerLabel}
                    value={getDateRange(
                      {
                        departureDate: firstFlight?.startDate,
                        arrivalDate: firstFlight?.endDate
                      },
                      !isFlightTypeRoundTrip
                    )}
                    onFocus={() => {
                      setIsCalendarOpen(true)
                      // analytics call for handleCalendarOnFocus(`datefield ${i}`)
                    }}
                    error={getDateFieldErrorMessage(
                      errors,
                      0,
                      isFlightTypeRoundTrip
                    )}
                    errorZIndex={1}
                    hideTooltip={
                      isCalendarOpen ||
                      ((isFlightTypeOneWay || isFlightTypeMultiDestination) &&
                        !!firstFlight.startDate) ||
                      (isFlightTypeRoundTrip &&
                        !!firstFlight.startDate &&
                        !!firstFlight.endDate)
                    }
                    isMobile={isMobile}
                    color={getDateFieldBorderColors(errors, 0)}
                    data-testid={`FLIGHTS_TRAVEL_DATES_INPUT_${0}`}
                    aria-label={datePickerAriaLabel}
                    aria-expanded={isCalendarOpen ? 'true' : 'false'}
                  />
                </Box>
                <CalendarWrapperBox>
                  <CalendarComponent
                    monthWidthPx={300}
                    id={`flight-date-range-calendar-${0}`}
                    onChange={(
                      _e: React.ChangeEvent,
                      dates: FlightDateType
                    ) => {
                      const { startDate = '', endDate = '' } = dates
                      const dateObj = {
                        startDate,
                        ...(isFlightTypeRoundTrip && { endDate })
                      }
                      setFlights(watchFlights, dateObj, setValue)
                      if (
                        !isFlightTypeRoundTrip ||
                        (dates.startDate && dates.endDate)
                      ) {
                        disableShadowState()
                        setIsCalendarOpen(false)
                      }
                    }}
                    animateScroll
                    minDate={getPrevDate(watchFlights, 0)}
                    startDate={firstFlight.startDate}
                    endDate={
                      isFlightTypeRoundTrip ? firstFlight.endDate : undefined
                    }
                    selectTwoDates={isFlightTypeRoundTrip}
                    allowSameDay={!isFlightTypeIncludeStay}
                    flightDetails={{
                      cabinClass: watchCabinClass,
                      flights: [
                        {
                          departureAirport,
                          arrivalAirport
                        }
                      ],
                      tripType: watchFlightType
                    }}
                    variantName="EXPRESS"
                    useDefaultFooter={isFlightTypeMultiDestination}
                    onDismiss={() => {
                      disableShadowState()
                      setIsCalendarOpen(false)
                      focusCalendarInput()
                    }}
                    useColorCode={!isFlightTypeMultiDestination}
                    callFlexDatesGraphUrl
                  />
                </CalendarWrapperBox>
              </PopoverContainer>
            )}
          </ShadowEffect>
        </Flex>
        {isFlightTypeMultiDestination && (
          <Box p={2} width={1}>
            <MultiDestination
              flights={watchFlights}
              setValue={setValue}
              errors={errors}
              isMobile={isMobile}
            />
          </Box>
        )}
        <Box
          width={[1, null, 1 / 2, null, showCabinClass ? 1 / 3 : 1 / 2]}
          p={2}
        >
          <ShadowEffect
            enabled
            forProperty="travelers-selection-readonly-input"
            alternativeBlurHandling
          >
            {({ disableShadowState }: { disableShadowState: () => void }) => (
              <TravelerSelection
                adultCount={watchAdultCount}
                childrenAges={watchChildrenAges}
                childrenCount={watchChildrenCount}
                disableShadowState={disableShadowState}
                disclaimer={FLIGHTS_DISCLAIMER}
                infantCount={watchInfantCount}
                isMobile={isMobile}
                onDoneButtonClick={disableShadowState}
                roomCount={watchRoomCount}
                setValue={setValue}
                showChildAgeSelectors={false}
                showInfantMenuItem
                showRoomMenuItem={watchTripType.includes(HOTEL)}
                showSubLabel
                showYouthMenuItem
                youthCount={watchYouthCount}
                error={
                  errors?.infantCount?.message ||
                  errors?.roomCount?.message ||
                  ''
                }
              />
            )}
          </ShadowEffect>
        </Box>
        {showCabinClass && (
          <Box width={[1, null, 1 / 2, null, 1 / 3]} p={2}>
            <ShadowEffect forProperty="cabin-class-select">
              {({ disableShadowState }: { disableShadowState: () => void }) => (
                <FormField>
                  <Label autoHide htmlFor="cabin-class-select">
                    Cabin Class
                  </Label>
                  <StyledSelect
                    isMobile={isMobile}
                    m={0}
                    id="cabin-class-select"
                    name="cabinClass"
                    value={watchCabinClass}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      setValue('cabinClass', e.target.value as CABIN_CLASS_TYPE)
                      disableShadowState()
                      /**  for GA tracking */
                    }}
                  >
                    {Object.keys(CABIN_CLASS).map(name => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </StyledSelect>
                </FormField>
              )}
            </ShadowEffect>
          </Box>
        )}
        {isMobile && (
          <Box width={1} p={2}>
            {showPackageOptions()}
          </Box>
        )}
        <StyledButtonContainer
          width={[
            1,
            null,
            showCabinClass ? 1 : 1 / 2,
            null,
            showCabinClass ? 1 / 3 : 1 / 2
          ]}
          p={2}
        >
          <SearchFormButton
            buttonText="Find Your Flight"
            data-testid="FLIGHTS_SUBMIT_BUTTON"
          />
          <Box
            style={{
              position:
                !isMobile && isFlightTypeRoundTrip ? 'absolute' : undefined
            }}
          >
            <Text fontSize={0} mt={isMobile ? '12px' : 2}>
              Book a flight with free cancellation for flexibility
            </Text>
          </Box>
        </StyledButtonContainer>
      </Flex>
      {!isMobile && (
        <Flex alignItems="center">
          <Box mt={2} mb={2}>
            {showPackageOptions()}
          </Box>
        </Flex>
      )}
    </form>
  )
}
